/**
 * SQUAD sidebar key/value rows — hide labels when the value is blank.
 * Used for Fondsdaten (p1), Weitere Daten + Fondspartner (p2).
 */

export type SquadKvRow = { label: string; value: string };

/** True when a listing value should not render a row. */
export function isSquadKvValueEmpty(value: unknown): boolean {
  const s = String(value ?? '')
    .replace(/\u00a0/g, ' ')
    .trim();
  if (!s) return true;
  // Common placeholders
  if (/^[-–—._]+$/.test(s)) return true;
  if (/^(n\/?a|k\.?\s*a\.?|none|leer)$/i.test(s)) return true;
  return false;
}

export function filterSquadKvRows(rows: SquadKvRow[]): SquadKvRow[] {
  return (rows || []).filter((row) => !isSquadKvValueEmpty(row?.value));
}

/** Map a string record + key list to non-empty KV rows. */
export function mapSquadKvRows(
  map: Record<string, unknown> | undefined | null,
  keys: Array<{ key: string; label: string }>,
): SquadKvRow[] {
  return filterSquadKvRows(
    keys.map(({ key, label }) => ({
      label,
      value: String(map?.[key] ?? ''),
    })),
  );
}

/**
 * Zahl und Einheit nicht trennen: „1,50 %“ brach im PDF so um, dass das „%“ allein in der
 * nächsten Zeile stand (Weekly 02.10.2026, Gebühren Seite 2). react-pdf und der Browser
 * brechen nicht an U+00A0 — der Umbruch fällt dann vor die Zahl.
 */
export function squadKeepNumberWithUnit(text: string): string {
  return String(text ?? '').replace(/(\d)[ \t]+(%|€|EUR\b)/g, '$1 $2');
}

/**
 * Labels mit führendem Spiegelstrich („- davon Verwaltungsgebühr p.a.“) bekommen einen
 * hängenden Einzug: der Strich in eine eigene schmale Spalte, der Rest daneben — so steht
 * die umbrochene Zeile bündig unter dem „d“ (Weekly 02.10.2026).
 * `columnEm`: Breite der Strich-Spalte = Strich + 0,22 em Luft (Segoe UI gemessen: „-“ 0,400,
 * „–“ 0,500, „—“ 1,000 em; ein Wortabstand wäre 0,274 em).
 */
export function splitSquadKvDash(label: string): { dash: string; rest: string; columnEm: number } | null {
  const m = /^([-–—])\s+(.+)$/s.exec(String(label ?? ''));
  if (!m) return null;
  const dashEm = m[1] === '—' ? 1 : m[1] === '–' ? 0.5 : 0.4;
  return { dash: m[1], rest: m[2], columnEm: dashEm + 0.22 };
}
